import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import StatCard from '../components/common/StatCard';
import Button from '../components/common/Button';
import Modal from '../components/common/Modal';
import { useNotification } from '../hooks/useNotification';
import {
  FiUsers,
  FiUserPlus,
  FiMail,
  FiMapPin,
  FiPhone,
  FiPlus,
  FiEye,
  FiDownload,
  FiMessageSquare,
  FiTrendingUp
} from 'react-icons/fi';

const ReferMerchantPage = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();

  const [referrals, setReferrals] = useState([
    {
      id: 1,
      date: '2025-01-15',
      referralToken: 'REF-2025-001',
      storeName: 'Al Noor Supermarket',
      contact: '+973-3344-5566',
      email: 'alnoor@example.com',
      location: 'Manama, Block 315',
      status: 'Pending'
    },
    {
      id: 2,
      date: '2025-01-10',
      referralToken: 'REF-2025-002',
      storeName: 'Fresh Fruits Market',
      contact: '+973-3355-6677',
      email: 'freshfruits@example.com',
      location: 'Muharraq, Road 12',
      status: 'Registered'
    },
    {
      id: 3,
      date: '2025-01-05',
      referralToken: 'REF-2025-003',
      storeName: 'City Laundry Services',
      contact: '+973-3366-7788',
      email: 'citylaundry@example.com',
      location: 'Riffa, Block 10',
      status: 'Active'
    },
  ]);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedReferral, setSelectedReferral] = useState(null);

  const [newStoreName, setNewStoreName] = useState('');
  const [newContact, setNewContact] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newLocation, setNewLocation] = useState('');

  const totalReferrals = referrals.length;
  const pendingReferrals = referrals.filter(r => r.status === 'Pending').length;
  const activeReferrals = referrals.filter(r => r.status === 'Active').length;
  const registeredReferrals = referrals.filter(r => r.status === 'Registered').length;

  const generateReferralToken = () => {
    const year = new Date().getFullYear();
    const num = String(referrals.length + 1).padStart(3, '0');
    return `REF-${year}-${num}`;
  };

  const handleAddReferral = () => {
    if (!newStoreName || !newContact || !newEmail || !newLocation) {
      showError('Please fill in all fields');
      return;
    }

    const newReferral = {
      id: referrals.length + 1,
      date: new Date().toISOString().split('T')[0],
      referralToken: generateReferralToken(),
      storeName: newStoreName,
      contact: newContact,
      email: newEmail,
      location: newLocation,
      status: 'Pending'
    };

    setReferrals([...referrals, newReferral]);
    showSuccess('Referral added successfully!');
    setShowAddModal(false);
    setNewStoreName('');
    setNewContact('');
    setNewEmail('');
    setNewLocation('');
  };

  const handleViewReferral = (referral) => {
    setSelectedReferral(referral);
    setShowViewModal(true);
  };

  const handleExport = (format) => {
    showSuccess(`Exporting referrals to ${format}...`);
  };

  return (
    <Layout title="Refer Merchant">
      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          icon={FiUsers}
          value={totalReferrals}
          label="Total Referrals"
          bgColor="bg-pastel-lavender"
          iconColor="text-white"
          iconBgColor="bg-primary-500"
        />
        <StatCard
          icon={FiUserPlus}
          value={pendingReferrals}
          label="Pending"
          bgColor="bg-pastel-peach"
          iconColor="text-white"
          iconBgColor="bg-warning-500"
        />
        <StatCard
          icon={FiTrendingUp}
          value={registeredReferrals}
          label="Registered"
          bgColor="bg-pastel-mint"
          iconColor="text-white"
          iconBgColor="bg-success-500"
        />
        <StatCard
          icon={FiUserPlus}
          value={activeReferrals}
          label="Active"
          bgColor="bg-pastel-pink"
          iconColor="text-white"
          iconBgColor="bg-danger-500"
        />
      </div>

      {/* Action Buttons */}
      <div className="mb-6 flex gap-4 items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-neutral-900">Referral Program</h2>
          <p className="text-sm text-neutral-600">Refer new merchants and track their registration status</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setShowAddModal(true)}>
            <FiPlus className="w-4 h-4 mr-2 inline" />
            Add Referral
          </Button>
          <div className="relative group">
            <Button variant="outline">
              <FiDownload className="w-4 h-4 mr-2 inline" />
              Export
            </Button>
            <div className="absolute right-0 mt-2 w-48 bg-white rounded-card shadow-lg border border-neutral-200 z-10 overflow-hidden hidden group-hover:block">
              <button
                onClick={() => handleExport('Excel')}
                className="block w-full text-left px-4 py-3 text-sm text-neutral-700 hover:bg-neutral-100 transition-colors"
              >
                Export to Excel
              </button>
              <button
                onClick={() => handleExport('PDF')}
                className="block w-full text-left px-4 py-3 text-sm text-neutral-700 hover:bg-neutral-100 transition-colors"
              >
                Export to PDF
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Referrals Table */}
      <div className="bg-white rounded-card shadow-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-neutral-50 border-b border-neutral-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-neutral-700 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-neutral-700 uppercase tracking-wider">
                  Referral Token
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-neutral-700 uppercase tracking-wider">
                  Store Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-neutral-700 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-neutral-700 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-neutral-700 uppercase tracking-wider">
                  Location
                </th>
                <th className="px-6 py-3 text-center text-xs font-semibold text-neutral-700 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-center text-xs font-semibold text-neutral-700 uppercase tracking-wider">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-200">
              {referrals.map((referral) => (
                <tr key={referral.id} className="hover:bg-neutral-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">
                    {new Date(referral.date).toLocaleDateString('en-BH')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm font-mono font-medium text-primary-600">
                      {referral.referralToken}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-neutral-900">{referral.storeName}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center text-sm text-neutral-600">
                      <FiPhone className="w-4 h-4 mr-2 text-neutral-400" />
                      {referral.contact}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center text-sm text-neutral-600">
                      <FiMail className="w-4 h-4 mr-2 text-neutral-400" />
                      {referral.email}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center text-sm text-neutral-600">
                      <FiMapPin className="w-4 h-4 mr-2 text-neutral-400" />
                      {referral.location}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      referral.status === 'Active' ? 'bg-success-100 text-success-800' :
                      referral.status === 'Registered' ? 'bg-primary-100 text-primary-800' :
                      'bg-warning-100 text-warning-800'
                    }`}>
                      {referral.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <button
                      onClick={() => handleViewReferral(referral)}
                      className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                      title="View Details"
                    >
                      <FiEye className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-gradient-to-br from-primary-500 to-primary-700 rounded-card p-6 text-white">
          <FiMessageSquare className="w-10 h-10 mb-4" />
          <h3 className="text-lg font-semibold mb-2">Need Support?</h3>
          <p className="text-sm mb-4 opacity-90">Contact us to learn more about the referral program</p>
          <Button variant="outline" size="sm" className="bg-white text-primary-600 hover:bg-opacity-90" onClick={() => navigate('/support')}>
            Contact Support
          </Button>
        </div>

        <div className="bg-gradient-to-br from-success-500 to-success-700 rounded-card p-6 text-white">
          <FiTrendingUp className="w-10 h-10 mb-4" />
          <h3 className="text-lg font-semibold mb-2">Advertise Now</h3>
          <p className="text-sm mb-4 opacity-90">Promote your business to reach more customers</p>
          <Button variant="outline" size="sm" className="bg-white text-success-600 hover:bg-opacity-90">
            Get Started
          </Button>
        </div>
      </div>

      {/* Add Referral Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setNewStoreName('');
          setNewContact('');
          setNewEmail('');
          setNewLocation('');
        }}
        title="Add New Referral"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Store Name
            </label>
            <input
              type="text"
              value={newStoreName}
              onChange={(e) => setNewStoreName(e.target.value)}
              placeholder="Enter store name"
              className="w-full px-4 py-2 border border-neutral-300 rounded-button focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Contact Number
            </label>
            <input
              type="text"
              value={newContact}
              onChange={(e) => setNewContact(e.target.value)}
              placeholder="+973-XXXX-XXXX"
              className="w-full px-4 py-2 border border-neutral-300 rounded-button focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Email Address
            </label>
            <input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="store@example.com"
              className="w-full px-4 py-2 border border-neutral-300 rounded-button focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Location
            </label>
            <input
              type="text"
              value={newLocation}
              onChange={(e) => setNewLocation(e.target.value)}
              placeholder="e.g., Manama, Block 315"
              className="w-full px-4 py-2 border border-neutral-300 rounded-button focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
            <p className="text-sm text-primary-800">
              <strong>Note:</strong> A referral token will be auto-generated after you submit this form.
            </p>
          </div>

          <div className="flex gap-3 justify-end pt-4">
            <Button
              variant="outline"
              onClick={() => {
                setShowAddModal(false);
                setNewStoreName('');
                setNewContact('');
                setNewEmail('');
                setNewLocation('');
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleAddReferral}>
              Add Referral
            </Button>
          </div>
        </div>
      </Modal>

      {/* View Referral Modal */}
      {selectedReferral && (
        <Modal
          isOpen={showViewModal}
          onClose={() => {
            setShowViewModal(false);
            setSelectedReferral(null);
          }}
          title="Referral Details"
        >
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-neutral-600">Date</label>
                <p className="text-sm text-neutral-900 mt-1">
                  {new Date(selectedReferral.date).toLocaleDateString('en-BH')}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-neutral-600">Referral Token</label>
                <p className="text-sm font-mono font-medium text-primary-600 mt-1">
                  {selectedReferral.referralToken}
                </p>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-neutral-600">Store Name</label>
              <p className="text-sm text-neutral-900 mt-1">{selectedReferral.storeName}</p>
            </div>

            <div>
              <label className="text-sm font-medium text-neutral-600">Contact</label>
              <p className="text-sm text-neutral-900 mt-1">{selectedReferral.contact}</p>
            </div>

            <div>
              <label className="text-sm font-medium text-neutral-600">Email</label>
              <p className="text-sm text-neutral-900 mt-1">{selectedReferral.email}</p>
            </div>

            <div>
              <label className="text-sm font-medium text-neutral-600">Location</label>
              <p className="text-sm text-neutral-900 mt-1">{selectedReferral.location}</p>
            </div>

            <div>
              <label className="text-sm font-medium text-neutral-600">Status</label>
              <p className="mt-1">
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                  selectedReferral.status === 'Active' ? 'bg-success-100 text-success-800' :
                  selectedReferral.status === 'Registered' ? 'bg-primary-100 text-primary-800' :
                  'bg-warning-100 text-warning-800'
                }`}>
                  {selectedReferral.status}
                </span>
              </p>
            </div>

            <div className="flex gap-3 justify-end pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setShowViewModal(false);
                  setSelectedReferral(null);
                }}
              >
                Close
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </Layout>
  );
};

export default ReferMerchantPage;
